// Mapview.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { formatAcquisitionDate } from "../layers/imagery/formatImageryDate";
import maplibregl from "maplibre-gl";
import MlDraw from "../lib/ml-draw/ml-draw.js";
import "../styles/maplibre-draw.css";

import MapLibreCompare from "@maplibre/maplibre-gl-compare";
import "@maplibre/maplibre-gl-compare/dist/maplibre-gl-compare.css";

import { useSyncFootprintLayers } from "../hooks/useSyncFootprintLayers";

import { useMapActions } from "../stores/useMapActions";
import { useAuthStore } from "../stores/useAuthStore";
import { useAoiStore } from "../stores/useAoiStore";
import { useLayersStore } from "../stores/useLayersStore";
import { useProjectStore } from "../stores/useProjectStore";
import { useLayerRegistryStore } from "../stores/useLayerRegistryStore";
import { useOperatingAoiContext } from "../stores/useOperatingAoiContext";
import { useAppViewStore } from "../stores/useAppViewStore";
import { useBasemapStore, basemapTileUrl } from "../stores/useBasemapStore";

import { throttle, createReusableCenter } from "../utils/mapSync";
import { aoiBoundsFlat } from "../utils/aoiBounds";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { useWellSelectionStore } from "../stores/useWellSelectionStore";
import { useCollabRailStore } from "../stores/useCollabRailStore";
import { useLayerStackManager } from "../hooks/useLayerStackManager";
import { bindTileAuthHandler } from "../api/tileTokens";
import { useSyncOperatingAoiImagery } from "../hooks/useSyncOperatingAoiImagery";
import { useSyncOperatingAoiCoverage } from "../hooks/useSyncOperatingAoiCoverage";
import { useSyncOperatingAoiSelection } from "../hooks/useSyncOperatingAoiSelection";
import { useTileProgressTracker } from "../hooks/useTileProgressTracker";
import { useSyncRegistryLayers } from "../hooks/useSyncRegistryLayers";
import { useSiteStore } from "../stores/useSiteStore";
import SiteLayer from "./siteid/SiteLayer";
import WellLayer from "./wellfinder/WellLayer";
import WellMapLegend from "./wellfinder/WellMapLegend";
import { focusWell } from "./wellfinder/cameraFocus";
import { hasClickPriorityHit } from "../constants/mapLayerIds";
import { useAppNav } from "../hooks/useAppNav";
import { useGloStore } from "../stores/useGloStore";
import { useCountyLayerStore } from "../stores/useCountyLayerStore";
import { useCountyNamesStore } from "../stores/useCountyNamesStore";
import { usePurchaseLayers } from "../hooks/usePurchaseLayers";
import { usePurchaseInteraction } from "../hooks/usePurchaseInteraction";
import PurchaseHud from "./purchase/PurchaseHud";

import { useAnnotationLayer } from "../hooks/useAnnotationLayer";
import { useAnnotationStore } from "../stores/useAnnotationStore";

import { usePadStore } from "../stores/usePadStore";
import PadLayer from "./padrekko/PadLayer";
import VectorFeatureLayers from "./features/VectorFeatureLayers";
import PadAccessRoadLayer from "./padrekko/PadAccessRoadLayer";
import PadRoadDrawer from "./padrekko/PadRoadDrawer";
import AnnotationGeometryEditor from "./annotations/AnnotationGeometryEditor";
import { useMeasure } from "../hooks/useMeasure";
import { useFlyTo } from "../hooks/useFlyTo";
import CommandRail from "./tools/CommandRail";
import PersistentMeasurementsLayer from "./padrekko/qc/PersistentMeasurementsLayer";
import MeasurementSaveHUD from "./padrekko/qc/MeasurementSaveHUD";
import MapAnnotationTool from "./padrekko/qc/MapAnnotationTool";
import { useQcStore } from "../stores/useQcStore";
import { useToolsUiStore } from "../stores/useToolsUiStore"

import { useImageryStackStore } from "../stores/useImageryStackStore";
import { useCoverageFlyoutStore } from "../stores/useCoverageFlyoutStore";
import { Columns2, SlidersHorizontal } from "lucide-react";
import FollowModeHUD from "./imagery/FollowModeHUD";

import TimeSlider from "./imagery/TimeSlider";

import { buildTransformRequest } from "../api/mapTransformRequest";
import { getConfig } from '../config';

const mapboxToken = () => getConfig().mapboxToken;

/* ------------------------------------------------------------------ */
/* HELPERS */
/* ------------------------------------------------------------------ */

function boundsFromGeometry(geom) {
  const coords = [];
  const walk = (c) =>
    typeof c[0] === "number" ? coords.push(c) : c.forEach(walk);
  walk(geom.coordinates);

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const [x, y] of coords) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  return [
    [minX, minY],
    [maxX, maxY],
  ];
}

// True while a modal draw tool owns the map click: measure, pad road, or an
// annotation tool from the My Layers "Create" panel. Each of these places a
// vertex wherever the user clicks, so the click must not also be read as a
// selection gesture by the layer handlers underneath.
//
// These tools do set claim flags on the event (_measureHandled,
// _annotationHandled), but those are useless to a handler registered before
// theirs: measure/road attach in mapLoaded-gated effects and the annotation
// click handler is registered after aoi-base-fill, while MapLibre fires
// listeners in registration order. Read the store state instead.
function hasModalDrawTool() {
  if (useToolsUiStore.getState().measureMode) return true;
  if (usePadStore.getState().drawingRoad) return true;
  const ann = useAnnotationStore.getState();
  if (ann.open && ann.tool) return true;
  return false;
}

/* ------------------------------------------------------------------ */
/* MAP VIEW */
/* ------------------------------------------------------------------ */

// ── THE CAMERA STOPS WHERE THE IMAGERY DOES ────────────────────────────────
//
// MapLibre's default map maxZoom is 22 (defaultMaxZoom in maplibre-gl) and the
// imagery raster sources are created with maxzoom from datasets.maxzoom, which
// is 20 for every current dataset. Above a raster source's maxzoom MapLibre does
// not request new tiles — it OVERZOOMS, scaling the z20 tiles up — so zooming to
// 22 magnified z20 pixels 4x while the readout kept counting. The picture
// stopped getting sharper two levels before the number stopped moving, and the
// "Zoom:" box does not say whose zoom it is reporting.
//
// THE CAP FOLLOWS THE DATA, because it is a property of the imagery and not of
// this file. datasets.maxzoom is derived at ingest from the ground resolution
// (10cm -> 20, 5cm -> 21; see maxzoom_for_resolution in imagery_ingest.py), so
// a hardcoded 20 would cap a 5cm collect one level short of what the customer
// paid for — and it would look like the tiler failing rather than a constant.
//
// FALLBACK RATHER THAN A GUESS. The map is built on mount and the catalog
// arrives asynchronously, so at construction there may be nothing to read. 20
// is the conservative choice: too low costs magnification the user can restore
// by reloading once the catalog lands, too high restores the overzoom this
// exists to stop. setMaxZoom is called once the catalog resolves.
//
// Vector sources are unaffected and keep maxzoom 22 in useSyncRegistryLayers —
// overzooming vector tiles is normal and lossless.
const IMAGERY_MAX_ZOOM_FALLBACK = 20;

export default function MapView({ onMapReady, onDrawReady, aoiModeActive }) {

  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  // For compare
  const mapBContainerRef = useRef(null);
  const mapBRef = useRef(null);
  const compareInstanceRef = useRef(null);
  const compareContainerRef = useRef(null);


  // The operating AOI last seen by the fitBounds effect below.
  //
  // The operating AOI is a session-level reference boundary — it should only
  // drive the viewport when the user explicitly CHANGES it mid-session, never
  // on mount, where it would throw away the configured initial center/zoom
  // (:441). Holds the id rather than a once-only boolean; see the effect for
  // why the boolean could not express that rule.
  const lastOperatingAoiRef = useRef(undefined);
  const [mapInstance, setMapInstance] = useState(null);
  const [drawInstance, setDrawInstance] = useState(null);

  const miniMapRef = useRef(null);
  const miniMapContainerRef = useRef(null);

  const drawRef = useRef(null);

  const hudTimerRef = useRef(null);

  const authTokenRef = useRef(null);
  const didInitRef = useRef(false);

  const purchaseActiveRef = useRef(false);

  /* ---------------- STATE ---------------- */

  const [mapLoaded, setMapLoaded] = useState(false);
  const [cursor, setCursor] = useState({ lat: 0, lng: 0 });
  const [zoom, setZoom] = useState(0);
  const [bearing, setBearing] = useState(0);
  // Map centre as [lng, lat], for the time strip's coverage lookup. Null until
  // the first move fires, which the strip renders as "Locating map view…"
  // rather than as "no coverage" — an unknown centre and an uncovered one are
  // different states and only one of them means the map is blank.
  const [mapCenter, setMapCenter] = useState(null);

  // The deepest zoom any entitled dataset actually has tiles for. See
  // IMAGERY_MAX_ZOOM_FALLBACK.
  const imageryCatalog = useImageryStackStore(s => s.catalog);
  const imageryMaxZoom = useMemo(() => {
    let best = 0;
    for (const series of imageryCatalog?.series ?? []) {
      for (const acq of series.acquisitions ?? []) {
        if (Number.isFinite(acq.maxzoom)) best = Math.max(best, acq.maxzoom);
      }
    }
    return best || IMAGERY_MAX_ZOOM_FALLBACK;
  }, [imageryCatalog]);
  const [olsPopup, setOlsPopup] = useState(null);

  const [hoverInfo, setHoverInfo] = useState(null);
  const hoverLockRef = useRef(false);

  const [barVisible, setBarVisible] = useState(false);
  const barHideTimerRef = useRef(null);
  const barFloorRef = useRef(0);

  /* ---------------- STORES ---------------- */

  const token = useAuthStore((s) => s.token);
  const activeBasemapId = useBasemapStore((s) => s.activeBasemapId);
  const aois = useAoiStore(s => s.aois);
  const activeAoiId = useAoiStore(s => s.activeAoiId);
  const loadAois = useAoiStore(s => s.loadAois);
  const hoveredAoiId = useAoiStore(s => s.hoveredAoiId);
  // Day 9 — polygon (vertex) edit mode. MapView subscribes here to flip
  // MlDraw into vertexEditMode when the AoiPanel's Edit Polygon affordance
  // or the AoiToolbar's Vertex mode button is engaged.
  const aoiPolygonEditMode = useAoiStore(s => s.aoiPolygonEditMode);
  const setActiveAoi = useAoiStore(s => s.setActiveAoi);

  const activeAoi = aois?.find((a) => a.id === activeAoiId) ?? null;

  const activeProjectId = useProjectStore((s) => s.activeProjectId);

  const setZoomHandler = useMapActions(s => s.setZoomHandler);

  const enabled = useLayersStore(s => s.assets.wells.enabled);
  const geometry = useLayersStore(s => s.assets.wells.geometry);
  const filters = useLayersStore(s => s.assets.wells.filters);

  // The two regulatory point layers. Separate asset keys, not geometry facets
  // of `wells` — see the note in layerPanelConfigStatic.js. Optional-chained
  // because a persisted store written before these keys existed rehydrates
  // without them, and an undefined read here would take the whole map down.
  const uicOn = useLayersStore(s => !!s.assets?.wells_uic?.geometry?.point);
  const permitsOn = useLayersStore(s => !!s.assets?.permits?.geometry?.point);
  const opacityById = useLayersStore(s => s.opacityById);

  const selectedWellIdRef = useRef(null);
  const selectWell = useWellSelectionStore(s => s.selectWell);
  const clearWell = useWellSelectionStore(s => s.clearWell);

  const closeRail = useCollabRailStore(s => s.closeRail);

  const olsVisible = useGloStore(s => s.olsVisible);
  const countiesVisible = useCountyLayerStore(s => s.countiesVisible);
  const countyNamesVisible = useCountyNamesStore(s => s.countyNamesVisible);

  const registryLayers = useLayerRegistryStore(s => s.layers);

  const handleMapClick_annotation = useAnnotationStore(s => s.handleMapClick);
  const closePendingPolygon = useAnnotationStore(s => s.closePendingPolygon);

  const { activeTab } = useAppNav()
  const activeProduct = activeTab ?? 'explore'
  const purchaseActive = activeProduct === 'purchase'
  // PadRekko case context — gates the persistent-measurement layer + save HUD.
  const qcSession = useQcStore((s) => s.session)
  const inPadrekkoCase = activeProduct === 'padrekko' && qcSession !== null

  const compareEnabled = useImageryStackStore(s => s.compareMode.enabled);
  const { initializeAnchors, addLayer, removeLayer } = useLayerStackManager(mapRef);
  const mapBStack = useLayerStackManager(mapBRef);
  const [mapBLoaded, setMapBLoaded] = useState(false);

  const mapBStackRef = useRef(null);
  mapBStackRef.current = mapBStack;


  // When the county coverage flyout is open we hide the change-detection
  // *controls* (the compare button and time strip) only — comparison
  // state in useImageryStackStore is untouched, so the active compare
  // instance and cursor position keep running and
  // reappear when the flyout closes. Inspecting coverage vs. comparing
  // imagery are different modes; you're in one or the other.
  const coverageFlyoutOpen = useCoverageFlyoutStore(s => s.isOpen);

  /* ------------------------------------------------------------------ */
  /* TILE PROGRESS HUD */
  /* ------------------------------------------------------------------ */

  const [tileProgress, setTileProgress] = useState({
    active: false,
    total: 0,
    loaded: 0
  });

  const [tileTelemetry, setTileTelemetry] = useState({
    hits: 0,
    misses: 0,
    tilesPerSec: 0,
    latencySum: 0,
    latencyCount: 0
  });

  const tileStateRef = useRef({
    pending: new Set(),
    loaded: new Set()
  });

  const latchedTotalRef = useRef(0);
  const tileRateRef = useRef({
    windowStart: performance.now(),
    loaded: 0
  });

  const [displayProgress, setDisplayProgress] = useState(0);

  const tileStartTimesRef = useRef(new Map());

  const activeOperatingAoiId = useOperatingAoiContext(
    s => s.activeOperatingAoiId
  );

  const scopeMode = useMemo(
    () => (activeOperatingAoiId ? "operating_aoi" : "global"),
    [activeOperatingAoiId]
  );

  const selectedSiteDetail = useSiteStore(
    (state) => state.selectedSiteDetail
  );

  useTileProgressTracker(
    mapRef.current,
    mapLoaded,
    setTileProgress,
    setTileTelemetry
  );

  const compareEnabledForSync = useImageryStackStore(s => s.compareMode.enabled);
  const leftLayerId = useImageryStackStore(s => s.compareMode.left_layer_id);
  const rightLayerId = useImageryStackStore(s => s.compareMode.right_layer_id);

  const leftPaneFilter = useMemo(() => (
    compareEnabledForSync
      ? { allowedImageryId: leftLayerId, hideNonImagery: true }
      : null
  ), [compareEnabledForSync, leftLayerId]);

  const rightPaneFilter = useMemo(() => (
    compareEnabledForSync
      ? { allowedImageryId: rightLayerId, hideNonImagery: true }
      : null
  ), [compareEnabledForSync, rightLayerId]);

  useSyncRegistryLayers(
    mapRef, mapLoaded, registryLayers, addLayer, removeLayer,
    activeOperatingAoiId, selectedSiteDetail,
    leftPaneFilter
  );

  useSyncRegistryLayers(
    mapBRef, mapBLoaded, registryLayers, mapBStack.addLayer, mapBStack.removeLayer,
    activeOperatingAoiId, null,
    rightPaneFilter
  );

  useAnnotationLayer(mapRef.current, mapLoaded);

  useEffect(() => {
    purchaseActiveRef.current = purchaseActive;
  }, [purchaseActive]);

  /* ------------------------------------------------------------------ */
  /* TOKEN REF */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    authTokenRef.current = token;
  }, [token]);

  // Window-level suppression of known-benign MapLibre internal errors.
  // These uncaught exceptions originate inside MapLibre's render loop
  // (draw_raster.ts in particular) when tile texture references are
  // disposed mid-frame. They're cosmetic — rendering recovers on next
  // frame and user-visible functionality is unaffected.
  useEffect(() => {
    const KNOWN_BENIGN = [
      /draw_raster.*Cannot read properties of undefined.*bind/,
      /Cannot read properties of undefined \(reading 'bind'\).*draw_raster/,
    ];

    function suppressBenign(event) {
      const stack = event.error?.stack ?? event.message ?? '';
      if (KNOWN_BENIGN.some(re => re.test(stack))) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return false;
      }
    }

    window.addEventListener('error', suppressBenign);
    return () => window.removeEventListener('error', suppressBenign);
  }, []);

  /* ------------------------------------------------------------------ */
  /* MAP INIT (ONCE) */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      // See IMAGERY_MAX_ZOOM: without this the camera runs to MapLibre's
      // default of 22 and overzooms the imagery for the last two levels.
      maxZoom: IMAGERY_MAX_ZOOM_FALLBACK,
      style: {
        version: 8,
        glyphs: "https://api.mapbox.com/fonts/v1/mapbox/{fontstack}/{range}.pbf?access_token=" + mapboxToken(),
        sources: {
          "mapbox-satellite": {
            type: "raster",
            tiles: [
              basemapTileUrl(useBasemapStore.getState().activeBasemapId)
            ],
            tileSize: 256,
          }
        },
        layers: [
          {
            id: "mapbox-satellite",
            type: "raster",
            source: "mapbox-satellite"
          }
        ]
      },
      center: [-102, 31],
      zoom: 8,
      attributionControl: false,
      maxzoom: 20,
      transformRequest: buildTransformRequest(() =>
        useAuthStore.getState().token
      ),
    });

    mapRef.current = map;

    window.__mapA = map;

    map.addControl(new maplibregl.NavigationControl(), "top-left");
    map.addControl(new maplibregl.GeolocateControl(), "top-left");

    map.on('error', (e) => {
      const msg = e.error?.message ?? '';
      // Known MapLibre-internal races: tile mid-decode invalidations,
      // raster draw cycle races. These are best-effort rendering issues
      // that don't affect user-visible functionality.
      const benign = [
        'could not be decoded',
        // Add more MapLibre-emitted benign messages here as discovered
      ];
      if (benign.some(p => msg.includes(p))) return;
      console.error('[map error]', e.error);
    });

    map.on("load", () => {
      if (didInitRef.current) return;
      didInitRef.current = true;
      bindTileAuthHandler(map);
      onMapReady?.(map);
      // Anchors can be created on load
      initializeAnchors();

      // Wait until the renderer is settled before adding dynamic sources/layers
      map.once("idle", () => {
        // SOURCES
        registerWellSources(map);

        // LAYERS (must go through stack manager)
        registerWellLayers(map, addLayer);

        // ── GLO OLS SURVEY GRID ──
        if (!map.getSource("glo-ols")) {
          map.addSource("glo-ols", {
            type: "vector",
            tiles: [`${window.location.origin}/vtiles/glo_ols/{z}/{x}/{y}.mvt`],
            minzoom: 7,
            maxzoom: 16,
          });

          // Grid outlines — always rendered, fill is transparent
          addLayer({
            group: "asset",
            layerConfig: {
              id: "glo-ols-outline",
              type: "line",
              source: "glo-ols",
              "source-layer": "glo_ols",
              minzoom: 9,
              layout: { visibility: "none" },
              paint: {
                "line-color": "#94a3b8",   // slate-400 — subtle on dark basemap
                "line-width": [
                  "interpolate", ["linear"], ["zoom"],
                  9, 0.4,
                  12, 0.8,
                  15, 1.2,
                ],
                "line-opacity": 0.6,
              },
            },
          });

          // Hover/highlight fill — toggled by well-click handler (filter set externally)
          addLayer({
            group: "asset",
            layerConfig: {
              id: "glo-ols-highlight",
              type: "fill",
              source: "glo-ols",
              "source-layer": "glo_ols",
              minzoom: 9,
              layout: { visibility: "none" },
              paint: {
                "fill-color": "#16f2d4",   // accent-teal
                "fill-opacity": 0.12,
                "fill-outline-color": "#16f2d4",
              },
              filter: ["==", "ols_id", ""],  // set dynamically on well click
            },
          });
        }

        // ── COUNTY BOUNDARIES ──
        // Reference overlay, mirrors the GLO grid block above. Source min/max
        // zoom is wider than GLO because counties are useful at state-level
        // zoom too; overzoom beyond source maxzoom 12 renders fine since the
        // backend ships full-fidelity geometry at z>=11.
        if (!map.getSource("counties")) {
          map.addSource("counties", {
            type: "vector",
            tiles: [`${window.location.origin}/vtiles/counties/{z}/{x}/{y}.mvt`],
            minzoom: 0,
            maxzoom: 12,
          });

          addLayer({
            group: "asset",
            layerConfig: {
              id: "counties-outline",
              type: "line",
              source: "counties",
              "source-layer": "counties",
              layout: { visibility: "none" },
              paint: {
                "line-color": "#5a6a7e",
                "line-width": [
                  "interpolate", ["linear"], ["zoom"],
                  4, 0.5,
                  8, 1.0,
                  12, 1.8,
                ],
                "line-opacity": 0.7,
              },
            },
          });

          // County NAME labels — second layer riding the same vector source.
          // Backend ships label points at all zooms; client enforces a z7
          // floor to avoid state-scale label soup, and MapLibre's collision
          // culling (text-allow-overlap: false) thins what's left.
          addLayer({
            group: "asset",
            layerConfig: {
              id: "counties-labels",
              type: "symbol",
              source: "counties",
              "source-layer": "counties_labels",
              minzoom: 7,
              layout: {
                visibility: "none",
                "text-field": ["get", "county"],
                "text-size": [
                  "interpolate", ["linear"], ["zoom"],
                  7, 10,
                  11, 13,
                  14, 15,
                ],
                // Matches the dominant convention in this codebase
                // (useSyncRegistryLayers.js, useAnnotationLayer.js). Both
                // glyphs are served by the Mapbox glyphs endpoint set in
                // the map style.
                "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
                "text-allow-overlap": false,
                "text-padding": 4,
              },
              paint: {
                "text-color": "#bab9b9",      // slate-300 — readable on dark basemap
                "text-halo-color": "#0f172a", // slate-900 halo for grid legibility
                "text-halo-width": 1.0,
              },
            },
          });
        }

        // ── AOI SOURCES (base + highlight) ──
        // USER AOIs ARE GREEN. Each kind of area on the map has its own hue:
        // user AOIs green, operating AOIs orange dashed, imagery coverage
        // yellow, the active imagery dataset / AOI blue. Base is every user
        // AOI; highlight is the hovered one, in the same green, so hovering
        // never borrows the "active" blue.
        if (!map.getSource("aoi-base")) {
          map.addSource("aoi-base", {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
          });
          map.addLayer({
            id: "aoi-base-fill",
            type: "fill",
            source: "aoi-base",
            paint: { "fill-color": "#22c55e", "fill-opacity": 0.05 },
          });
          map.addLayer({
            id: "aoi-base-line",
            type: "line",
            source: "aoi-base",
            paint: { "line-color": "#22c55e", "line-width": 1, "line-opacity": 0.8 },
          });
        }

        if (!map.getSource("aoi-highlight")) {
          map.addSource("aoi-highlight", {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
          });
          map.addLayer({
            id: "aoi-highlight-fill",
            type: "fill",
            source: "aoi-highlight",
            paint: { "fill-color": "#22c55e", "fill-opacity": 0.25 },
          });
          map.addLayer({
            id: "aoi-highlight-line",
            type: "line",
            source: "aoi-highlight",
            paint: { "line-color": "#22c55e", "line-width": 2.5, "line-opacity": 0.9 },
          });
        }

        // ── MLDRAW INIT ──
        const draw = new MlDraw();
        map.addControl(draw);
        drawRef.current = draw;
        setDrawInstance(draw);
        // Phase 1 vertex-edit smoke test — dev-only window pin so the
        // console can drive enterVertexEdit/exitVertexEdit without
        // needing React DevTools. Remove after smoke test lands.
        if (import.meta.env.DEV) {
          window.__mlDraw = draw;
        }

        // Right-click to close polygon or line
        map.on("contextmenu", (e) => {
          const d = drawRef.current;
          if (!d || !d.drawing) return;
          if (d.mode === "polygon") {
            d.completePolygon?.([e.lngLat.lng, e.lngLat.lat]);
          } else if (d.mode === "line") {
            d.completeLine?.([e.lngLat.lng, e.lngLat.lat]);
          }
        });

        // Day 9 — click an AOI polygon on the map to select it (same
        // outcome as clicking the row in AoiPanel). Reads the latest
        // store action via getState so this binding doesn't capture a
        // stale closure. Active AOI's polygon is rendered by both
        // aoi-base-fill AND MlDraw's final-fill; the click here lands
        // on aoi-base, not on the MlDraw layer. When MlDraw is in
        // vertex-edit mode and the user clicks the polygon outline,
        // ml-draw-final-line owns that event (vertex insert) — they
        // don't conflict.
        map.on("click", "aoi-base-fill", (e) => {
          if (!e.features || !e.features.length) return;
          const aoiId = e.features[0].properties?.id;
          if (!aoiId) return;
          // Skip if currently in vertex-edit mode — that gesture is
          // owned by ml-draw's edge-click vertex insert.
          const editingId = useAoiStore.getState().aoiPolygonEditMode;
          if (editingId) return;
          // Yield to a modal draw tool — the click belongs to the tool, not
          // to AOI selection. Without this, every measure vertex / road
          // vertex / annotation placed inside the AOI also selects the AOI
          // and fitBounds() the camera back out to it.
          if (hasModalDrawTool()) return;
          // Yield to any feature drawn on top of the AOI fill (wells, pads,
          // SiteID sites). Without this the AOI select + fitBounds fires on
          // the same click and yanks the camera back out to the AOI — the
          // AOI fill covers the whole working area, so every feature click
          // inside it is also an AOI click as far as MapLibre is concerned.
          if (hasClickPriorityHit(map, e.point)) return;
          // Selecting fires the existing 1318 effect which loads the
          // polygon into MlDraw with bbox transform handles; we also
          // zoom to the AOI's bounds for visual focus.
          useAoiStore.getState().setActiveAoi(aoiId);
          const aoi = useAoiStore.getState().aois.find(a => a.id === aoiId);
          if (aoi?.geom) {
            const bounds = aoiBoundsFlat(aoi.geom);
            const zoom = useMapActions.getState().zoomToBounds;
            if (bounds && zoom) zoom(bounds);
          }
        });
        // Visual cue: pointer cursor when hovering an AOI fill.
        map.on("mouseenter", "aoi-base-fill", () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", "aoi-base-fill", () => {
          map.getCanvas().style.cursor = "";
        });

        map.on("contextmenu", async (e) => {
          // Only active when Survey Grid is toggled on
          if (!useGloStore.getState().olsVisible) return;

          // Suppress browser context menu
          e.preventDefault?.();

          const { lng, lat } = e.lngLat;
          const { x, y } = e.point;

          try {
            const res = await fetchWithAuth(
              `/api/v1/glo/ols/point?lon=${lng}&lat=${lat}`
            );
            if (!res.ok) return;

            const data = await res.json();
            if (data.error) return;

            setOlsPopup({
              x,
              y,
              ols_id: data.ols_id,
              survey_name: data.survey_name,
              block: data.block,
              section: data.section,
              subdivision: data.subdivision,
              abstract_label: data.abstract_label,
              region: data.region,
            });

            if (map.getLayer("glo-ols-highlight")) {
              map.setFilter("glo-ols-highlight", ["==", "ols_id", data.ols_id]);
            }

          } catch (err) {
            console.error("[glo/ols popup]", err);
          }
        });

        // Dismiss popup on left-click anywhere on the map
        map.on("click", () => {
          setOlsPopup(null);
        });

        // Expose draw instance to MapPage
        if (onDrawReady) {
          draw.onUpdate = () => { }; // MapPage sets this via handleDrawReady
          onDrawReady(draw);
        }

        // NOTE: footprints + operating AOI layers are created by their own code paths
        // but must not run until mapLoaded is true.
        setMapLoaded(true);
        setMapInstance(map);
        // Mirror to the global view store so panel-mounted consumers
        // (Padrekko QC drawer, etc.) can grab the map without props
        // plumbed up through their parent panels. MapView is the truth
        // owner; this is a mirror, not a hand-off. See useAppViewStore.
        useAppViewStore.getState().setMapInstance(map);

        // SEED THE CENTRE, because "move" may never fire. A user who loads the
        // map and pans nothing would otherwise leave the time strip on
        // "Locating map view…" indefinitely — the strip would be reporting a
        // state that is true only until the first frame, on a map that is
        // already showing imagery.
        const c0 = map.getCenter();
        setMapCenter([c0.lng, c0.lat]);
      });

      map.on("mousemove", (e) => {
        setCursor({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      });

      map.on("move", () => {
        setZoom(map.getZoom());
        setBearing(map.getBearing());
        // The time strip resolves coverage at the CENTRE, per move. Unthrottled
        // like the two setters above it: the check is ~47us over the whole
        // catalog, and debouncing would make the strip lag the map during
        // exactly the pan in which a user looks at it to ask why the map is
        // blank. As an array so the prop is value-comparable.
        const c = map.getCenter();
        setMapCenter([c.lng, c.lat]);
      });

      map.on("click", async (e) => {
        if (e.originalEvent._measureHandled) return;
        if (!useAnnotationStore.getState().open) return;
        const tool = useAnnotationStore.getState().tool;
        if (!tool) return;

        // Don't let annotation clicks fall through to well selection
        e.originalEvent._annotationHandled = true;

        const lngLat = e.lngLat;

        if (tool === "note") {
          const text = window.prompt("Note text:");
          if (!text?.trim()) return;
          useAnnotationStore.getState().handleMapClick(lngLat, text.trim());
          return;
        }

        useAnnotationStore.getState().handleMapClick(lngLat);
      });

      // Double-click ends a polygon OR a line. One gesture for both, because
      // they are the same act — "this shape is finished" — and a line that
      // ended on its own after two clicks was the reason a path had to be drawn
      // as a pile of separate segments.
      //
      // preventDefault suppresses the map's dblclick-zoom, which would
      // otherwise fire underneath and jump the view the moment a shape closed.
      map.on("dblclick", (e) => {
        const st = useAnnotationStore.getState();
        if (!st.open) return;
        if (st.tool !== "polygon" && st.tool !== "line") return;
        e.preventDefault();
        if (st.tool === "polygon") st.closePendingPolygon();
        else st.closePendingLine();
      });

      map.on("click", "rrc-well-points", (e) => {
        if (e.originalEvent._annotationHandled) return;
        if (e.originalEvent._measureHandled) return;
        if (purchaseActiveRef.current) return;
        if (!e.features?.length) return;

        const f = e.features[0];
        const wellId = f.properties?.id;
        if (!wellId) return;

        selectedWellIdRef.current = wellId;

        selectWell({
          id: wellId,
          properties: f.properties,
          lngLat: e.lngLat,
        });

        // ── Focus the clicked well ──
        // Every product gets this: selectWell above opens WellInfoPanel (a
        // 340px absolute overlay, rendered from MapPage for any selection),
        // so panelWillOpen is always true here. focusWell is shared with
        // WellFinder so the zoom floor and panel left-shift stay identical
        // across explore and well_finder. Feature geometry over e.lngLat so
        // the well centers exactly, not wherever the cursor landed.
        //
        // In well_finder, WellLayer's selection effect may focus the same
        // record again once its detail fetch resolves — same coords, so the
        // second ease is a no-op in practice.
        const geo = f.geometry?.coordinates;
        const coords = (Array.isArray(geo) && typeof geo[0] === "number")
          ? geo                                   // Point — [lng, lat]
          : [e.lngLat.lng, e.lngLat.lat];         // anything else — use the click
        focusWell(map, coords, { panelWillOpen: true, duration: 600 });

        // ── Highlight enclosing OLS section ──
        const olsId = f.properties?.ols_id;
        if (olsId && map.getLayer("glo-ols-highlight")) {
          map.setFilter("glo-ols-highlight", ["==", "ols_id", olsId]);
        }
      });

      // Clear OLS highlight when clicking away from a well (existing clear handler):
      map.on("click", (e) => {
        if (e.originalEvent._measureHandled) return;
        if (purchaseActiveRef.current) return;
        const hits = map.queryRenderedFeatures(e.point, {
          layers: ["rrc-well-points"],
        });
        if (hits.length) return;

        setOlsPopup(null);

        if (selectedWellIdRef.current) {
          selectedWellIdRef.current = null;
          clearWell();
        }

        // ── Clear OLS highlight ──
        if (map.getLayer("glo-ols-highlight")) {
          map.setFilter("glo-ols-highlight", ["==", "ols_id", ""]);
        }
      });

      map.on("click", (e) => {
        const hits = map.queryRenderedFeatures(e.point);

        const lateral = hits.find(
          f => f.properties?.asset_type === "rrc_well_lateral"
        );

        if (!lateral) return;

      });

      map.on("mouseenter", "rrc-well-points", () => {
        if (useToolsUiStore.getState().measureMode) return;
        if (usePadStore.getState().drawingRoad) return;
        if (useAnnotationStore.getState().open && useAnnotationStore.getState().tool) {
          map.getCanvas().style.cursor = "crosshair";
          return;
        }
        map.getCanvas().style.cursor = "pointer";
      });

      map.on("mouseleave", "rrc-well-points", () => {
        if (useToolsUiStore.getState().measureMode) return;
        if (usePadStore.getState().drawingRoad) return;
        if (useAnnotationStore.getState().open && useAnnotationStore.getState().tool) {
          map.getCanvas().style.cursor = "crosshair";
          return;
        }
        map.getCanvas().style.cursor = "";
      });

      // ── Regulatory points: hover only, NO CLICK HANDLER ────────────────
      //
      // The rrc-well-points click above calls selectWell(), which opens
      // WellInfoPanel against a PRODUCING-well record. A UIC well has no
      // producing record and a permit has no well yet, so routing either into
      // that panel would open an empty or wrong detail view — worse than not
      // being clickable. Detail views for these two are their own change.
      //
      // The cursor therefore stays default rather than becoming a pointer,
      // because a pointer promises a click that does nothing. `crosshair`
      // during annotation is preserved so the drawing tools read consistently
      // over every layer.
      ["rrc-uic-points", "rrc-permit-points"].forEach((layerId) => {
        map.on("mouseenter", layerId, () => {
          if (useToolsUiStore.getState().measureMode) return;
          if (usePadStore.getState().drawingRoad) return;
          if (useAnnotationStore.getState().open && useAnnotationStore.getState().tool) {
            map.getCanvas().style.cursor = "crosshair";
          }
        });
        map.on("mouseleave", layerId, () => {
          if (useToolsUiStore.getState().measureMode) return;
          if (usePadStore.getState().drawingRoad) return;
          if (useAnnotationStore.getState().open && useAnnotationStore.getState().tool) {
            map.getCanvas().style.cursor = "crosshair";
            return;
          }
          map.getCanvas().style.cursor = "";
        });
      });

      initMiniMap(map, miniMapRef);
      removeImproveMapLink();
    });

    // ── CHANGED: skip well cursor logic when purchase mode is active ──
    map.on("mousemove", (e) => {
      if (purchaseActiveRef.current) return;
      if (usePadStore.getState().drawingRoad) return;
      if (useToolsUiStore.getState().measureMode) return;

      const hits = map.queryRenderedFeatures(e.point);

      let overSurface = false;
      let overLateral = false;

      for (const f of hits) {
        const t = f.properties?.asset_type;
        if (t === "rrc_well_surface") overSurface = true;
        if (t === "rrc_well_lateral") overLateral = true;
      }

      if (overLateral) {
        map.getCanvas().style.cursor = "crosshair"; // or "alias"
      } else if (overSurface) {
        map.getCanvas().style.cursor = "pointer";
      } else {
        map.getCanvas().style.cursor = "";
      }
    });

    return () => {
      map.remove();
      mapRef.current = null;
      miniMapRef.current?.remove();
      miniMapRef.current = null;
    };
  }, []);

  // ── BASEMAP SELECTION ──
  // Swaps the tiles on the existing "mapbox-satellite" source rather than
  // calling map.setStyle() — a full style swap would wipe every dynamically
  // added source/layer/anchor (wells, AOIs, PadLayer, etc.). This is the
  // same technique usePurchaseLayers.js already uses to force satellite on
  // entering purchase mode. Skipped while purchase mode is active — it owns
  // this source for its session and restores whatever was active on exit,
  // so no explicit hand-off is needed here.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    if (purchaseActive) return;

    const url = basemapTileUrl(activeBasemapId);

    const src = map.getSource("mapbox-satellite");
    if (src?.setTiles) src.setTiles([url]);

    const miniSrc = miniMapRef.current?.getSource?.("mapbox-satellite");
    if (miniSrc?.setTiles) miniSrc.setTiles([url]);
  }, [activeBasemapId, mapLoaded, purchaseActive]);

  // ── CHANGED: gate OLS visibility on !purchaseActive ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    if (!map.getLayer("glo-ols-outline")) return;
    if (purchaseActive) return;  // ← ADDED: purchase mode owns layer visibility

    const vis = olsVisible ? "visible" : "none";
    map.setLayoutProperty("glo-ols-outline", "visibility", vis);
    map.setLayoutProperty("glo-ols-highlight", "visibility", vis);
  }, [olsVisible, mapLoaded, purchaseActive]);  // ← ADDED purchaseActive dep

  // County-boundary visibility — same shape as the OLS effect, including the
  // purchaseActive guard (purchase mode owns its own layer visibility).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    if (!map.getLayer("counties-outline")) return;
    if (purchaseActive) return;

    map.setLayoutProperty(
      "counties-outline",
      "visibility",
      countiesVisible ? "visible" : "none"
    );
  }, [countiesVisible, mapLoaded, purchaseActive]);

  // County-NAME visibility — independent of the boundary line layer. Reads
  // its own store so the mutual-exclusion logic in ReferenceTab (counties
  // vs GLO grid) does not affect labels. Labels are valid with county
  // lines, with the survey grid, or with neither.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    if (!map.getLayer("counties-labels")) return;
    if (purchaseActive) return;

    map.setLayoutProperty(
      "counties-labels",
      "visibility",
      countyNamesVisible ? "visible" : "none"
    );
  }, [countyNamesVisible, mapLoaded, purchaseActive]);

  useEffect(() => {
    if (!tileProgress.total) return;
    const target =
      (tileProgress.loaded / tileProgress.total) * 100;
    const id = requestAnimationFrame(() => {
      setDisplayProgress(prev => prev + (target - prev) * 0.25);
    });
    return () => cancelAnimationFrame(id);
  }, [tileProgress.loaded, tileProgress.total]);

  /* ------------------------------------------------------------------ */
  /* COMPARE MODE — Lazy mapB + Compare instance lifecycle */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const mapA = mapRef.current;
    if (!mapA) return;

    if (compareEnabled) {
      if (mapBRef.current) return;
      if (!mapBContainerRef.current) return;
      if (!compareContainerRef.current) return;

      const mapB = new maplibregl.Map({
        container: mapBContainerRef.current,
        // MUST MATCH mapA. Compare syncs the two cameras; if one clamps at 20
        // and the other runs to 22 they desync at the top of the range, which
        // is exactly where a side-by-side comparison is looked at closely.
        maxZoom: imageryMaxZoom,
        style: mapA.getStyle(),
        center: mapA.getCenter(),
        zoom: mapA.getZoom(),
        bearing: mapA.getBearing(),
        pitch: mapA.getPitch(),
        attributionControl: false,
        transformRequest: buildTransformRequest(() =>
          useAuthStore.getState().token
        ),
      });

      mapBRef.current = mapB;

      window.__mapB = mapB;

      mapB.on("load", () => {
        bindTileAuthHandler(mapB);  // ← ADD
      });

      // The Compare library handles pan/zoom sync internally via
      // @mapbox/mapbox-gl-sync-move. Don't add our own listeners — that
      // creates a cascade that degrades performance.

      const attachCompare = () => {
        const compare = new MapLibreCompare(mapA, mapB, compareContainerRef.current, {
          orientation: "vertical",
          mousemove: false,
        });
        compareInstanceRef.current = compare;

        const savedPosition = useImageryStackStore.getState().compareMode.slider_position;
        if (savedPosition != null) {
          const width = compareContainerRef.current.clientWidth;
          compare.setSlider(savedPosition * width);
        }

        compare.on("slideend", () => {
          const width = compareContainerRef.current.clientWidth;
          const px = compare.currentPosition ?? 0;
          const ratio = width > 0 ? px / width : 0.5;
          useImageryStackStore.getState().setCompareSliderPosition(ratio);
        });
      };

      const onMapBReady = () => {
        // Initialize the group anchors on mapB so addLayer can route layers
        // to the right position in mapB's style.
        mapBStackRef.current.initializeAnchors();
        setMapBLoaded(true);
        attachCompare();
      };

      if (mapB.loaded()) {
        onMapBReady();
      } else {
        mapB.once("load", onMapBReady);
      }

      return () => {
        setMapBLoaded(false);
        if (compareInstanceRef.current) {
          compareInstanceRef.current.remove();
          compareInstanceRef.current = null;
        }
        mapB.remove();
        mapBRef.current = null;
      };
    }
  }, [compareEnabled]);

  // Apply the catalog-derived cap once it is known. The map is constructed with
  // the fallback because the catalog is not loaded yet at that point.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !imageryMaxZoom) return;
    if (map.getMaxZoom() === imageryMaxZoom) return;
    map.setMaxZoom(imageryMaxZoom);
    mapBRef.current?.setMaxZoom(imageryMaxZoom);
  }, [imageryMaxZoom, mapLoaded]);

  /* ------------------------------------------------------------------ */
  /* FOOTPRINT SYNC                                                     */
  /* ── CHANGED: disabled in purchase mode ──                           */
  /* ------------------------------------------------------------------ */
  useSyncFootprintLayers({
    map: mapLoaded ? mapRef.current : undefined,
    footprintsUrl: "/imagery/footprints?lod=fine",
    addLayer,
    disabled: purchaseActive,
  });

  /* ------------------------------------------------------------------ */
  /* OPERATING AOI IMAGERY SYNC */
  /* ------------------------------------------------------------------ */
  useSyncOperatingAoiImagery();
  useSyncOperatingAoiCoverage();
  useSyncOperatingAoiSelection();

  const measure = useMeasure({
    map: mapRef.current,
    mapLoaded,
  });

  const { flyToCoords, flyToPlace, flyToBounds } = useFlyTo({ map: mapRef.current });

  /* ------------------------------------------------------------------ */
  /* PURCHASE LAYERS + INTERACTION                                      */
  /* ------------------------------------------------------------------ */
  usePurchaseLayers({
    map: mapRef.current,
    mapLoaded,
    addLayer,
    purchaseActive,
  });

  usePurchaseInteraction({
    map: mapRef.current,
    mapLoaded,
    purchaseActive,
  });

  /* ------------------------------------------------------------------ */
  /* WELL VISIBILITY + FILTERS                                          */
  /* ── CHANGED: skip when purchase mode is active ──                   */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const map = mapRef.current;

    if (!map || !map.isStyleLoaded()) return;
    if (!map.getLayer("rrc-well-points")) return;
    if (purchaseActive) return;  // ← ADDED: purchase mode owns layer visibility

    const POINT_LAYER_ID = "rrc-well-points";
    const LINE_LAYER_ID = "rrc-well-laterals";

    // OPACITY IS THE USER'S SLIDER, NOT A SECOND OFF-SWITCH.
    //
    // These two used to fold visibility into the opacity value — `enabled &&
    // geometry.point ? opacityById[...] ?? 1.0 : 0` — so an off layer was BOTH
    // visibility:"none" AND circle-opacity:0. Two switches for one state, and
    // they could come apart: the styledata re-sync below writes visibility and
    // nothing else, so a layer turned on while the style was still loading came
    // back visible with its fill opacity still 0.
    //
    // THAT IS THE "black outline" REPORT. circle-opacity paints only the FILL;
    // circle-stroke-opacity is a separate property that nothing here ever wrote,
    // so it sat at the registered 1.0. A surface well with an invisible orange
    // fill and a fully opaque 1.2px #000 stroke renders as a black ring — which
    // is exactly what appeared when WellFinder turned the wells on and the main
    // effect had bailed on `!map.isStyleLoaded()`, leaving styledata to restore
    // visibility alone. Toggling from the RRC panel afterwards ran this effect
    // with the style loaded and the wells went orange again, which is why it
    // looked like two different styles for the same layer.
    //
    // visibility is now the only off-switch and opacity carries only what the
    // user asked for, so the two cannot disagree.
    const pointOpacity = opacityById[POINT_LAYER_ID] ?? 1.0;
    const lineOpacity = opacityById[LINE_LAYER_ID] ?? 1.0;

    map.setLayoutProperty(
      "rrc-well-points",
      "visibility",
      enabled && geometry.point ? "visible" : "none"
    );

    map.setPaintProperty(
      "rrc-well-points",
      "circle-opacity",
      pointOpacity
    );

    // BOTH HALVES OF THE MARK. Setting the fill alone meant dragging the
    // opacity slider down left the black stroke at full strength — the same
    // defect as above in its mild form, a ring darkening as its fill faded.
    map.setPaintProperty(
      "rrc-well-points",
      "circle-stroke-opacity",
      pointOpacity
    );

    map.setLayoutProperty(
      "rrc-well-laterals",
      "visibility",
      enabled && geometry.line ? "visible" : "none"
    );

    map.setPaintProperty(
      "rrc-well-laterals",
      "line-opacity",
      lineOpacity
    );

    const f = ["all"];

    if (Array.isArray(filters) && filters.length > 0) {
      f.push([
        "in",
        ["coalesce", ["get", "status_norm"], "__none__"],
        ["literal", filters]
      ]);
    }

    if (scopeMode === "user_aoi" && activeAoi?.geometry) {
      f.push(["within", activeAoi.geometry]);
    }

    map.setFilter("rrc-well-points", f);
    map.setFilter("rrc-well-laterals", f);
  }, [
    enabled,
    geometry.point,
    geometry.line,
    filters,
    opacityById,
    scopeMode,
    activeAoi?.id,
    purchaseActive,  // ← ADDED
  ]);

  // ── CHANGED: gate styledata re-sync on !purchaseActive via ref ──
  // Re-run well visibility sync once style finishes loading —
  // handles the case where applySnapshot fires before the map is ready.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // VISIBILITY ONLY, AND THAT IS NOW SUFFICIENT. This handler writes no paint,
    // which was a live bug while the effect above encoded "off" as opacity 0 as
    // well — re-showing a layer here restored visibility and left the fill
    // transparent. Opacity is no longer a visibility channel, so the one
    // property this handler writes is the whole of the state it re-syncs.
    function onStyleLoad() {
      if (!map.getLayer("rrc-well-points")) return;
      if (purchaseActiveRef.current) return;  // ← ADDED
      map.setLayoutProperty(
        "rrc-well-points", "visibility",
        enabled && geometry.point ? "visible" : "none"
      );
      map.setLayoutProperty(
        "rrc-well-laterals", "visibility",
        enabled && geometry.line ? "visible" : "none"
      );

      // Same re-sync for the regulatory pair. Guarded individually because
      // registerWellLayers may not have run for them on an older cached style.
      if (map.getLayer("rrc-uic-points")) {
        map.setLayoutProperty(
          "rrc-uic-points", "visibility",
          uicOn ? "visible" : "none"
        );
      }
      if (map.getLayer("rrc-permit-points")) {
        map.setLayoutProperty(
          "rrc-permit-points", "visibility",
          permitsOn ? "visible" : "none"
        );
      }
    }

    map.on("styledata", onStyleLoad);
    return () => map.off("styledata", onStyleLoad);
  }, [enabled, geometry.point, geometry.line, uicOn, permitsOn]);

  /* ------------------------------------------------------------------ */
  /* WELL TILE SCOPING (AOI) */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const map = mapRef.current;

    if (!map) return;

    const suffix =
      scopeMode === "operating_aoi" && activeOperatingAoiId
        ? `&aoi=${activeOperatingAoiId}`
        : "";

    const pts = map.getSource("rrc-well-points");
    const lns = map.getSource("rrc-well-laterals");

    if (pts?.setTiles) {
      pts.setTiles([
        `${window.location.origin}/vtiles/rrc_well_surface/{z}/{x}/{y}.pbf?source=RRC${suffix}`,
      ]);
    }

    if (lns?.setTiles) {
      lns.setTiles([
        `${window.location.origin}/vtiles/rrc_well_lateral/{z}/{x}/{y}.pbf?source=RRC${suffix}`,
      ]);
    }

    const uic = map.getSource("rrc-uic-points");
    const per = map.getSource("rrc-permit-points");

    if (uic?.setTiles) {
      uic.setTiles([
        `${window.location.origin}/vtiles/rrc_well_uic/{z}/{x}/{y}.pbf?source=RRC${suffix}`,
      ]);
    }

    if (per?.setTiles) {
      per.setTiles([
        `${window.location.origin}/vtiles/rrc_permit/{z}/{x}/{y}.pbf?source=RRC${suffix}`,
      ]);
    }
  }, [mapLoaded, scopeMode, activeOperatingAoiId]);

  /* ------------------------------------------------------------------ */
  /* REGULATORY POINT VISIBILITY (UIC, permits)                          */
  /* ------------------------------------------------------------------ */
  //
  // A PARALLEL EFFECT RATHER THAN AN EXTENSION OF THE WELL ONE, deliberately.
  // The effect above at "WELL VISIBILITY" also owns opacity and the status
  // filter, both of which are producing-well concepts: status_norm is populated
  // by the RRC ingest for producers only, and opacityById is keyed on the two
  // well layer ids. Folding two filterless layers into it would mean widening
  // its dependency array and its filter construction for cases that never
  // apply, which is the refactor this change is avoiding.
  //
  // purchaseActive is honored for the same reason the well effect honors it:
  // purchase mode owns layer visibility outright while it is on.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    if (purchaseActive) return;

    if (map.getLayer("rrc-uic-points")) {
      map.setLayoutProperty(
        "rrc-uic-points", "visibility",
        uicOn ? "visible" : "none"
      );
    }

    if (map.getLayer("rrc-permit-points")) {
      map.setLayoutProperty(
        "rrc-permit-points", "visibility",
        permitsOn ? "visible" : "none"
      );
    }
  }, [uicOn, permitsOn, purchaseActive, mapLoaded]);

  /* ------------------------------------------------------------------ */
  /* OPERATING AOI                                                       */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    let cancelled = false;

    async function run() {
      if (!activeOperatingAoiId) {
        ["operating-aoi-fill", "operating-aoi-outline"].forEach((id) => {
          if (map.getLayer(id)) map.removeLayer(id);
        });
        if (map.getSource("operating-aoi")) map.removeSource("operating-aoi");
        return;
      }

      const res = await fetchWithAuth(
        `/api/v1/operating-aois/${activeOperatingAoiId}/geometry`
      );
      if (!res.ok || cancelled) return;

      const { geom } = await res.json();
      if (!geom || cancelled) return;

      const feature = { type: "Feature", geometry: geom };

      if (!map.getSource("operating-aoi")) {
        map.addSource("operating-aoi", {
          type: "geojson",
          data: feature,
        });

        addLayer({
          group: "aoi",
          layerConfig: {
            id: "operating-aoi-fill",
            type: "fill",
            source: "operating-aoi",
            paint: {
              "fill-color": "#c2651a",   // muted orange
              "fill-opacity": 0.04,      // very subtle fill
            }
          }
        });

        addLayer({
          group: "aoi",
          layerConfig: {
            id: "operating-aoi-outline",
            type: "line",
            source: "operating-aoi",
            paint: {
              "line-color": "#c2651a",   // unchanged — same orange as the fill
              // Prominence comes from opacity and width, not from the hue. At
              // 0.5 the outline read as a washed-out brown against imagery
              // rather than as the orange it is; the colour was never the
              // problem, the veil over it was.
              "line-width": 2.5,
              "line-opacity": 0.95,
              // KEPT. The dash is semantic, not decoration — it says this
              // boundary is reference geometry the operator cannot edit here,
              // which is still true at full strength.
              "line-dasharray": [4, 3],
            }
          }
        });

      } else {
        map.getSource("operating-aoi").setData(feature);
      }

      const bounds = boundsFromGeometry(geom);

      // ZOOM ONLY ON A REAL SWITCH, AND KEY IT ON THE ID.
      //
      // This was a once-only boolean that cleared itself after the first
      // successful run. Every LATER run then fitted the camera -- including a
      // re-run for the SAME operating AOI, which is not a switch and which the
      // rule above says must not move the viewport. React StrictMode mounts
      // every effect twice in development, so that second run was guaranteed
      // there, and the symptom was the map ignoring its configured center and
      // zoom (:441) on first load and framing the operating AOI instead.
      //
      // Comparing ids states the actual rule: undefined means "no AOI seen
      // yet", so the first one to arrive never zooms however many times the
      // effect runs, and only a change from one non-null id to a different one
      // does. Re-running for the same id is a no-op, which is what makes this
      // safe under a double mount.
      const previousOperatingAoiId = lastOperatingAoiRef.current;
      if (
        previousOperatingAoiId !== undefined &&
        previousOperatingAoiId !== activeOperatingAoiId
      ) {
        map.fitBounds(bounds, {
          padding: 80,
          maxZoom: 14,
          duration: 600,
        });
      }
      lastOperatingAoiRef.current = activeOperatingAoiId;
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [activeOperatingAoiId, mapLoaded]);

  // ── SYNC AOIs → base layer ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const src = map.getSource("aoi-base");
    if (!src) return;

    src.setData({
      type: "FeatureCollection",
      features: (aois ?? [])
        .filter(a => a.geom)
        .map(a => ({
          type: "Feature",
          geometry: a.geom,
          properties: { id: a.id },
        })),
    });
  }, [aois, mapLoaded]);

  // ── SYNC hover AOI → highlight layer ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const src = map.getSource("aoi-highlight");
    if (!src) return;

    if (!hoveredAoiId) {
      src.setData({ type: "FeatureCollection", features: [] });
      return;
    }
    const aoi = (aois ?? []).find(a => a.id === hoveredAoiId);
    if (!aoi?.geom) return;
    src.setData({
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: aoi.geom, properties: {} }],
    });
  }, [hoveredAoiId, aois, mapLoaded]);

  // ── SYNC active AOI → MlDraw (load geometry into draw on selection) ──
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw || !mapLoaded) return;
    // Only manage the AOI draw inside the editing context. Gating on
    // aoiModeActive (panel visibility) also makes this effect re-fire when the
    // user returns to AOI mode with the SAME activeAoiId — without it, the geom
    // wouldn't reload after a round-trip through another product (3c), since
    // the leave-AOI-mode effect below clears the draw.
    if (!aoiModeActive) return;

    draw.stopDrawing?.();
    draw.setHandleMode?.("none");
    draw.delete?.();

    const selected = (aois ?? []).find(a => a.id === activeAoiId);
    if (!selected?.geom) return;

    draw.add({
      type: "Feature",
      geometry: selected.geom,
      properties: {},
    });
  }, [aois, activeAoiId, mapLoaded, aoiModeActive]);

  // ── Day 9 — SYNC aoiPolygonEditMode → MlDraw vertex-edit ──
  // Runs after the load-into-draw effect above. When polygon edit is
  // requested, flip MlDraw into vertexEditMode (hides bbox transforms,
  // shows per-vertex handles + halo). When cleared, revert to bbox
  // transforms (or no handles if no active AOI). Effect is keyed on
  // activeAoiId too so swapping which AOI is being edited refreshes
  // handles after the load-into-draw effect runs.
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw || !mapLoaded) return;
    if (aoiPolygonEditMode && aoiPolygonEditMode === activeAoiId) {
      draw.enterVertexEdit?.();
    } else {
      draw.exitVertexEdit?.();
    }
  }, [aoiPolygonEditMode, activeAoiId, mapLoaded]);

  // ── CLEAR draw on AOI mode exit ──
  // (aoiModeActive is passed from MapPage — false when user leaves AOI mode)
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw || aoiModeActive) return;
    draw.stopDrawing?.();
    draw.delete?.();
    draw.lastFeature = null;
  }, [aoiModeActive]);

  /*
    useEffect(() => {
      console.debug("[WELLS SCOPE]", {
        scopeMode,
        activeOperatingAoiId,
        activeAoiId: activeAoi?.id,
      });
    }, [scopeMode, activeOperatingAoiId, activeAoi?.id]);
  */

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    setZoomHandler((bounds) => {
      if (!mapRef.current) return;
      if (!Array.isArray(bounds) || bounds.length !== 4) return;
      mapRef.current.fitBounds(
        [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
        { padding: 60, duration: 600, maxZoom: 18 }
      );
    });
  }, [mapLoaded]);


  //
  // COLLAB RAIL
  //
  useEffect(() => {
    closeRail();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const { id } = e.detail ?? {};
      if (!id || !mapRef.current) return;
      try {
        if (mapRef.current.getLayer(id)) {
          mapRef.current.setLayoutProperty(id, "visibility", "none");
        }
      } catch (_) { }
    };
    window.addEventListener("aiprime:hide-layer", handler);
    return () => window.removeEventListener("aiprime:hide-layer", handler);
  }, []);


  // ── effect: drive fade in/out from tileProgress.active ──
  // Keyed to tileProgress.active ONLY. barVisible is read via the
  // functional setter / ref-style guard, never depended on, so the
  // 400ms unmount timer firing can't re-trigger this effect and
  // interleave with a rapid active→inactive→active sequence.
  useEffect(() => {
    if (tileProgress.active) {
      // Rising edge of a cycle = either the bar wasn't showing yet, or the
      // previous cycle's unmount timer is still pending (back-to-back loads
      // where the new cycle starts within the 400ms fade window). In both
      // cases the floor belongs to the OLD cycle and must reset to 0 so the
      // new cycle's bar starts empty rather than inheriting 100%.
      if (barHideTimerRef.current || !barVisible) {
        barFloorRef.current = 0;
      }
      // Loading started/ongoing: cancel any pending hide, ensure mounted.
      if (barHideTimerRef.current) {
        clearTimeout(barHideTimerRef.current);
        barHideTimerRef.current = null;
      }
      setBarVisible(true);
    } else {
      // Loading ended: hold mounted at width 100 (barWidth handles that),
      // fade opacity to 0, then unmount after the fade completes. Guard
      // against scheduling a second timer if one is already pending.
      if (!barHideTimerRef.current) {
        barHideTimerRef.current = setTimeout(() => {
          setBarVisible(false);
          barFloorRef.current = 0;
          barHideTimerRef.current = null;
        }, 400); // MUST equal the opacity transition duration below
      }
    }
    return () => {
      if (barHideTimerRef.current) {
        clearTimeout(barHideTimerRef.current);
        barHideTimerRef.current = null;
      }
    };
  }, [tileProgress.active]);

  function registerWellSources(map) {

    // console.log("[REGISTER WELL SOURCES] called");

    if (!map.getSource("rrc-well-points")) {
      map.addSource("rrc-well-points", {
        type: "vector",
        tiles: [
          `${window.location.origin}/vtiles/rrc_well_surface/{z}/{x}/{y}.pbf?source=RRC`
        ],
        minzoom: 11,
        maxzoom: 18
      });
    }

    if (!map.getSource("rrc-well-laterals")) {
      map.addSource("rrc-well-laterals", {
        type: "vector",
        tiles: [
          `${window.location.origin}/vtiles/rrc_well_lateral/{z}/{x}/{y}.pbf?source=RRC`
        ],
        minzoom: 11,
        maxzoom: 18
      });
    }

    // Same minzoom/maxzoom as the producing pair, deliberately. 23,115 UIC
    // wells and 1,512 permits are small beside 980,962 surface wells, which
    // already render acceptably under this floor, so neither needs its own.
    if (!map.getSource("rrc-uic-points")) {
      map.addSource("rrc-uic-points", {
        type: "vector",
        tiles: [
          `${window.location.origin}/vtiles/rrc_well_uic/{z}/{x}/{y}.pbf?source=RRC`
        ],
        minzoom: 11,
        maxzoom: 18
      });
    }

    if (!map.getSource("rrc-permit-points")) {
      map.addSource("rrc-permit-points", {
        type: "vector",
        tiles: [
          `${window.location.origin}/vtiles/rrc_permit/{z}/{x}/{y}.pbf?source=RRC`
        ],
        minzoom: 11,
        maxzoom: 18
      });
    }
  }

  function registerWellLayers(map, addLayer) {

    addLayer({
      group: "asset",
      layerConfig: {
        id: "rrc-well-laterals",
        type: "line",
        source: "rrc-well-laterals",
        "source-layer": "assets",   // REQUIRED
        minzoom: 11,
        layout: {
          visibility: "none",
        },
        paint: {
          "line-color": "#fda261",
          "line-width": ["interpolate", ["linear"], ["zoom"], 11, 1, 15, 3],
          "line-opacity": 1.0
        }
      }
    });

    addLayer({
      group: "asset",
      layerConfig: {
        id: "rrc-well-points",
        type: "circle",
        source: "rrc-well-points",
        "source-layer": "assets",   // REQUIRED
        minzoom: 11,
        layout: {
          visibility: "none",
        },
        paint: {
          "circle-radius": 4,
          "circle-color": "#f97316",
          "circle-opacity": 1.0,

          "circle-stroke-color": "#000000",
          "circle-stroke-width": 1.2,
          "circle-stroke-opacity": 1.0
        }
      }
    });

    // ── Injection and disposal ──────────────────────────────────────────
    //
    // Sky against the producer orange. Cool reads as a different KIND of well
    // rather than a different state of one, which is what these are: fluid
    // going into the ground instead of coming out. The water association is
    // not accidental — saltwater disposal is most of this population.
    addLayer({
      group: "asset",
      layerConfig: {
        id: "rrc-uic-points",
        type: "circle",
        source: "rrc-uic-points",
        "source-layer": "assets",   // REQUIRED
        minzoom: 11,
        layout: {
          visibility: "none",
        },
        paint: {
          "circle-radius": 4,
          "circle-color": "#38bdf8",
          "circle-opacity": 1.0,

          "circle-stroke-color": "#000000",
          "circle-stroke-width": 1.2,
          "circle-stroke-opacity": 1.0
        }
      }
    });

    // ── Permitted locations ─────────────────────────────────────────────
    //
    // The producer hue, outline only. A permit is the SAME OBJECT at an earlier
    // lifecycle stage, so sharing #f97316 and differing in treatment says "this
    // well, not yet" where a second color would say "a different kind of
    // thing". Transparent fill rather than a lighter shade because tint alone
    // does not survive being drawn over imagery.
    addLayer({
      group: "asset",
      layerConfig: {
        id: "rrc-permit-points",
        type: "circle",
        source: "rrc-permit-points",
        "source-layer": "assets",   // REQUIRED
        minzoom: 11,
        layout: {
          visibility: "none",
        },
        paint: {
          "circle-radius": 4,
          "circle-color": "#f97316",
          "circle-opacity": 0,

          "circle-stroke-color": "#f97316",
          "circle-stroke-width": 1.5,
          "circle-stroke-opacity": 1.0
        }
      }
    });
  }

  const percent = Math.min(
    100,
    Math.round((tileProgress.loaded / tileProgress.total) * 100)
  );

  const avgLatency =
    tileTelemetry.latencyCount > 0
      ? Math.round(tileTelemetry.latencySum / tileTelemetry.latencyCount)
      : 0;

  const hitRate =
    tileTelemetry.hits + tileTelemetry.misses > 0
      ? Math.round(
        (tileTelemetry.hits /
          (tileTelemetry.hits + tileTelemetry.misses)) * 100
      )
      : 0;

  // ── Imagery progress: latched denominator + completion snap ──
  if (tileProgress.active) {
    latchedTotalRef.current = Math.max(
      latchedTotalRef.current,
      tileProgress.total
    );
  } else {
    latchedTotalRef.current = 0;
  }

  const latchedPct =
    latchedTotalRef.current > 0
      ? Math.min(100, (tileProgress.loaded / latchedTotalRef.current) * 100)
      : 0;

  // While loading: eased motion, but never lag behind real latched
  // progress. On completion (active false, bar still mounted for the
  // fade): hard 100 — bypasses the easing curve, which cannot reach
  // 100 within the tracker's brief idle-snap window.
  const rawBarWidth = tileProgress.active ? latchedPct : 100;

  // Displayed width never moves backward while the bar is visible.
  // Real progress is non-monotonic during denominator discovery (esp.
  // zoom-out, which restarts the cycle then re-discovers a large total);
  // clamping the *pixels* monotonic turns a trust-destroying retreat
  // into a brief stall. Floor resets only when the bar fully unmounts.
  if (rawBarWidth >= barFloorRef.current || !tileProgress.active) {
    barFloorRef.current = rawBarWidth;
  }
  const barWidth = barFloorRef.current;

  /* ------------------------------------------------------------------ */
  /* RENDER */
  /* ------------------------------------------------------------------ */

  return (
    <div className="absolute inset-0">
      <div ref={compareContainerRef} className="absolute inset-0">
        <div ref={mapContainerRef} className="absolute inset-0" />
        <div
          ref={mapBContainerRef}
          className={`absolute inset-0 ${compareEnabled ? "" : "hidden"}`}
        />
      </div>
      {mapInstance && activeProduct === "siteid" && <SiteLayer map={mapInstance} />}

      {mapInstance && activeProduct === "well_finder" && <WellLayer map={mapInstance} />}

      {/* PadLayer renders in every mode so the Features-tab "Well Pads"
          toggle works regardless of product. Visibility is gated by
          useLayersStore.assets.pads (defaults on); the click/hover/select
          handlers update usePadStore state silently in non-Padrekko
          modes — the selection becomes visible the next time the operator
          enters Padrekko. */}
      {mapInstance && <PadLayer map={mapInstance} addLayer={addLayer} />}

      {/* Generic vector_assets feature layers — the FEATURES-tab derived flat
          list. Renders in every mode (toggles persist across products);
          well_pad is excluded here (handled by PadLayer). */}
      {mapInstance && <VectorFeatureLayers map={mapInstance} addLayer={addLayer} />}

      {/* Access roads + road-drawer remain Padrekko-scoped — those are
          product-specific affordances, not catalog layers. */}
      {mapInstance && activeProduct === "padrekko" && (
        <>
          <PadAccessRoadLayer map={mapInstance} addLayer={addLayer} />
          <PadRoadDrawer drawInstance={drawInstance} />
        </>
      )}

      {/* Third consumer of the shared MlDraw instance, after the AOI
          effects above and PadRoadDrawer. Not product-scoped — the
          annotation panel is reachable from My Layers in any product. It
          yields to AOI mode; see the header of the component. */}
      <AnnotationGeometryEditor
        drawInstance={drawInstance}
        aoiModeActive={aoiModeActive}
      />

      {/* Footprint Hover Popup */}
      {hoverInfo && (
        <div
          className="map-hover pointer-events-auto"
          style={{
            position: "fixed",
            left: hoverInfo.point.x,
            top: hoverInfo.point.y,
            zIndex: 100,
          }}
          onMouseEnter={() => (hoverLockRef.current = true)}
          onMouseLeave={() => {
            hoverLockRef.current = false;
            setHoverInfo(null);
          }}
        >
          <div className="bg-slate-900/90 border border-slate-700 rounded-md px-3 py-2 text-[11px] text-gray-300 shadow-lg shadow-black/10 min-w-[220px]">
            <div className="font-medium text-white text-[12px]">
              {hoverInfo.props.name}
            </div>

            <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
              <div className="text-gray-400">Resolution</div>
              <div className="text-right">10 cm RGB</div>

              <div className="text-gray-400">Region</div>
              <div className="text-right capitalize">
                {hoverInfo.props.region}
              </div>

              <div className="text-gray-400">Acquired</div>
              <div className="text-right">
                {hoverInfo.props.acquisition_date}
              </div>
            </div>

            <div className="mt-1 text-[10px] text-gray-400">
              Imagery available
            </div>

            <button
              className="mt-2 w-full rounded-sm border border-accent-action/60 text-accent-action hover:bg-accent-action/10 text-[11px] py-1"
              onClick={(e) => {
                e.stopPropagation();
                toggleImagery(hoverInfo.id);
                setHoverInfo(null);
              }}
            >
              Show Imagery
            </button>
          </div>
        </div>
      )}

      {olsPopup && (
        <div
          className="pointer-events-auto"
          style={{
            position: "fixed",
            left: olsPopup.x + 12,
            top: olsPopup.y - 8,
            zIndex: 200,
          }}
        >
          <div className="bg-slate-900/95 border border-slate-700 rounded-md
                        px-3 py-2.5 text-[11px] text-gray-300 shadow-xl
                        shadow-black/40 min-w-[180px]">

            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">
                Survey Grid
              </span>
              <button
                onClick={() => {
                  setOlsPopup(null);
                  const map = mapRef.current;
                  if (map?.getLayer("glo-ols-highlight")) {
                    map.setFilter("glo-ols-highlight", ["==", "ols_id", ""]);
                  }
                }}
                className="text-gray-600 hover:text-gray-300 transition text-xs leading-none ml-3"
              >
                ✕
              </button>
            </div>

            {/* Fields */}
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">

              <div className="text-gray-500">Survey</div>
              <div className="text-right font-medium text-gray-200 truncate max-w-[110px]">
                {olsPopup.survey_name ?? "—"}
              </div>

              <div className="text-gray-500">Block</div>
              <div className="text-right font-medium text-gray-200">
                {olsPopup.block || "—"}
              </div>

              <div className="text-gray-500">Section</div>
              <div className="text-right font-medium text-gray-200">
                {olsPopup.section || "—"}
              </div>

              {olsPopup.subdivision && (
                <>
                  <div className="text-gray-500">Subdivision</div>
                  <div className="text-right font-medium text-gray-200">
                    {olsPopup.subdivision}
                  </div>
                </>
              )}

              <div className="text-gray-500">Abstract</div>
              <div className="text-right text-gray-400">
                {olsPopup.abstract_label ?? "—"}
              </div>

              <div className="text-gray-500">Region</div>
              <div className="text-right text-gray-400 capitalize">
                {olsPopup.region ?? "—"}
              </div>

            </div>
          </div>
        </div>
      )}

      {mapLoaded && !coverageFlyoutOpen && (
        <CommandRail measure={measure} onFlyToPlace={flyToPlace} onFlyToBounds={flyToBounds} annotationsEnabled={inPadrekkoCase} />
      )}

      {/* PadRekko persistent measurements — render saved annotations on the map,
          the save HUD, and the Annotations tool, only inside a loaded case
          (Explore mode untouched). */}
      {inPadrekkoCase && <PersistentMeasurementsLayer />}
      {inPadrekkoCase && <MeasurementSaveHUD measure={measure} />}
      {inPadrekkoCase && <MapAnnotationTool />}

      {purchaseActive && <PurchaseHud />}


      {/* HUD & Minimap */}

      {/* ------------------------------------------------------------------ */}
      {/* TILE PROGRESS HUD */}
      {/* ------------------------------------------------------------------ */}

      {/* Imagery load progress — thin top line. Invisible until loading
          starts (fade in), tracks progress, fades back out on completion. */}
      {barVisible && (
        <div className="absolute top-0 left-0 right-0 h-[5px] z-[2] pointer-events-none">
          <div
            className="h-full bg-accent-info ease-out shadow-[0_0_6px_rgba(22,242,212,0.6)]"
            style={{
              width: `${barWidth}%`,
              opacity: tileProgress.active ? 1 : 0,
              // width animates fast (responsive progress), opacity fades slow
              // (gentle dissolve). 400ms here MUST match the setTimeout above.
              transitionProperty: "width, opacity",
              transitionTimingFunction: "ease-out",
              transitionDuration: "200ms, 400ms",
            }}
          />
        </div>
      )}

      {/*
      {tileProgress.active && (
        <div
          className="absolute bottom-[85px] left-1/2 -translate-x-1/2 bg-slate-900/90
         border border-slate-700 rounded-md px-4 py-1.5 shadow-xl
         pointer-events-none flex items-center gap-4 w-auto"
        >
          <div className="text-[11px] text-gray-300 uppercase tracking-wide whitespace-nowrap">
            Imagery Loading ...
          </div>

          <div className="w-32 h-1.5 bg-slate-700 rounded overflow-hidden">
            <div
              className="h-full bg-accent-info transition-all duration-200"
              style={{ width: `${percent}%` }}
            />
          </div>

          <div className="text-[10px] text-gray-400 whitespace-nowrap">
            {tileProgress.loaded} / {tileProgress.total}
          </div>
        </div>

      )}
      */}

      <div className="absolute inset-0 pointer-events-none">
        {/* Change-detection controls. Hidden while the county coverage
            flyout is open — see coverageFlyoutOpen note above. The compare
            instance and cursor position are unaffected
            because they live in useImageryStackStore, not here. */}
        {!coverageFlyoutOpen && (
          <>
            {/* Compare button — top-left, below MapLibre's zoom controls */}
            <div className="absolute top-[145px] left-[10px] pointer-events-auto flex flex-col gap-1.5">
              <CompareModeButton />
            </div>
          </>
        )}
        {/* BOTTOM STACK: lat/lng readout above, time strip below, both flush
            to the map's left and right edges with a 5px margin.

            ONE FLEX COLUMN, NOT TWO POSITIONED BOXES. The readout has to sit
            above the strip, and doing that with a hardcoded bottom offset means
            a magic number that tracks the strip's rendered height -- silently
            invalidated by any padding or font change inside TimeSlider, with a
            readout overlapping the strip as the symptom and nothing in the
            suite to catch it. Stacking them makes the offset the browser's job.

            The strip stays inside the coverage-flyout condition and the readout
            stays outside it: opening the flyout hides the strip, and the
            readout drops to the bottom of the same stack. The plaque has always
            been unconditional and still is.

            The "DREAMHAMMER RGB 10cm" plaque that used to sit at left-[120px]
            is GONE: a string literal that read nothing and was read by nothing,
            whose only positioning constraint was clearing this box. */}
        <div className="absolute bottom-[5px] left-[5px] right-[5px] flex flex-col gap-[5px] pointer-events-none">
          {/* Above the readout, in the same stack. Gated on the same condition
              as WellLayer so the legend cannot outlive the layers it
              describes — a legend for absent encodings is the same defect as a
              wrong one. */}
          {activeProduct === "well_finder" && <WellMapLegend />}

          {/* One row: the lat/lng readout on the left, the Follow Mode pill
              on the right, both directly above the time strip. items-end
              keeps the pill level with the readout's bottom edge, the one
              nearest the strip. */}
          <div className="flex items-end justify-between gap-2">
            <div className="bg-slate-900/70 border border-slate-700 rounded-md px-2 py-1 text-[10px] text-gray-300 shadow-lg shadow-black/40">
              <div>Lat: {cursor.lat.toFixed(5)}</div>
              <div>Lng: {cursor.lng.toFixed(5)}</div>
              <div className="text-accent-info">Zoom: {zoom.toFixed(2)}</div>
            </div>
            <FollowModeHUD />
          </div>

          {!coverageFlyoutOpen && <TimeSlider center={mapCenter} />}
        </div>

        <div className="absolute top-[10px] right-3 pointer-events-auto z-[80]">
          <div className="relative w-40 h-40 rounded-md overflow-hidden border border-slate-700 shadow-xl shadow-black/60 bg-slate-900">
            <div ref={miniMapContainerRef} className="w-full h-full" />
            <div className="absolute inset-0 border border-accent-info/70 rounded-md pointer-events-none" />
          </div>
        </div>

        <div className="absolute top-[180px] right-3">
          <div className="w-10 h-10 rounded-full border border-slate-700 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center relative shadow-lg shadow-black/30">
            {/* Cardinal markers */}
            <div className="absolute top-0.5 text-[9px] font-medium text-red-400">N</div>
            <div className="absolute bottom-0.5 text-[9px] text-gray-500">S</div>
            <div className="absolute left-1 text-[9px] text-gray-500">W</div>
            <div className="absolute right-1 text-[9px] text-gray-500">E</div>
            {/* Needle — rotates around center, spans the compass radius */}
            <div
              className="relative w-0.5 h-5 origin-center"
              style={{ transform: `rotate(${bearing}deg)` }}
            >
              {/* Top half — red (points north) */}
              <div className="absolute top-0 left-0 w-full h-1/2 bg-red-500 rounded-t-sm" />
              {/* Bottom half — gray (tail) */}
              <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gray-500 rounded-b-sm" />
            </div>
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-bg/80 to-transparent z-[1]" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* MINIMAP HELPERS */
/* ------------------------------------------------------------------ */

function initMiniMap(mainMap, miniMapRef) {
  const miniMapContainer = document.querySelector(
    ".absolute.top-\\[10px\\].right-3 .w-full.h-full"
  );
  if (!miniMapContainer) return;

  const miniMap = new maplibregl.Map({
    container: miniMapContainer,
    style: {
      version: 8,
      sources: {
        "mapbox-satellite": {
          type: "raster",
          tiles: [
            `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/256/{z}/{x}/{y}@2x?access_token=${mapboxToken()}`
          ],
          tileSize: 256,
        }
      },
      layers: [
        {
          id: "mapbox-satellite",
          type: "raster",
          source: "mapbox-satellite"
        }
      ]
    },
    center: mainMap.getCenter(),
    zoom: Math.max(mainMap.getZoom() - 4, 1),
    attributionControl: false,
  });

  if (miniMapRef) miniMapRef.current = miniMap;

  miniMap.on("load", () => {
    if (!miniMap.getSource("viewport")) {
      miniMap.addSource("viewport", {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: []
          }
        }
      });

      miniMap.addLayer({
        id: "viewport-outline",
        type: "line",
        source: "viewport",
        paint: {
          "line-color": "#4bee22",
          "line-width": 2
        }
      });

      miniMap.addLayer({
        id: "viewport-fill",
        type: "fill",
        source: "viewport",
        paint: {
          "fill-color": "#22d3ee",
          "fill-opacity": 0.08
        }
      }, "viewport-outline");
    }
  });

  const center = createReusableCenter();

  const syncFast = throttle(() => {
    if (!miniMap || !mainMap) return;

    const c = mainMap.getCenter();

    miniMap.jumpTo({
      center: [c.lng, c.lat],
      zoom: Math.max(mainMap.getZoom() - 4, 1),
      bearing: mainMap.getBearing(),
      pitch: 0
    });

    const b = mainMap.getBounds();

    const boundsPoly = {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [[
          [b.getWest(), b.getSouth()],
          [b.getEast(), b.getSouth()],
          [b.getEast(), b.getNorth()],
          [b.getWest(), b.getNorth()],
          [b.getWest(), b.getSouth()]
        ]]
      }
    };

    const src = miniMap.getSource("viewport");
    if (src) {
      src.setData(boundsPoly);
    }

  }, 33);

  mainMap.on("move", syncFast);
}

function removeImproveMapLink() {
  const interval = setInterval(() => {
    document
      .querySelectorAll(".maplibregl-ctrl-attrib-inner a")
      .forEach((el) => el.remove());
  }, 300);
  setTimeout(() => clearInterval(interval), 3000);
}

// EXIT ONLY. Compare is ENTERED from the time strip, which knows the dates and
// which of them cover the view; this button used to enter it too, gated on
// `stack.length >= 2`, which meant the only route in was switching on two
// imagery layers by hand in the legacy IMAGERY panel. That gate is what kept
// the panel load-bearing. It renders solely while compare is active, as the way
// out of a mode whose way in is somewhere else.
function CompareModeButton() {
  const compareMode = useImageryStackStore(s => s.compareMode);
  const exitCompareMode = useImageryStackStore(s => s.exitCompareMode);

  const isActive = compareMode.enabled;
  if (!isActive) return null;

  return (
    <button
      onClick={() => exitCompareMode()}
      className={`
        w-[29px] h-[29px] flex items-center justify-center
        rounded-sm border transition shadow-md
        ${isActive
          ? "border-accent-action/60 text-accent-action"
          : "bg-white/95 border-slate-300 text-slate-700 hover:bg-white"
        }
      `}
      title="Exit compare"
    >
      <Columns2 size={15} />
    </button>
  );
}

function formatAcquisitionDateInline(d) {
  return formatAcquisitionDate(d);
}